import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FormInput } from '../../components';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { User } from '../../interfaces/User';
import Select from 'react-select';
import { GenderOptions } from '../../interfaces/Client';
import { APICore } from '../../utils/api/apiCore';
import Modules from '../../utils/modules';
import FileUploader from '../../components/FileUploader';
import { FileType } from '../../interfaces/FileType';
import InsuranceCompanyAgentManager from '../../components/InsuranceCompanyAgentManager';
interface AddUserProps {
    show: boolean;
    onHide?: () => void;
    onAddUser?: (newUser: User, oldUser?: User, file?: FileType) => void;
    user?: User;
    assignmentCandidates?: User[];
}

interface Option {
    value: string;
    label: string;
}

const AddUser = ({ show, onHide, onAddUser, user, assignmentCandidates = [] }: AddUserProps) => {
    const [currentUser, setCurrentUser] = useState<User>();
    const [currentUserImageUrl, setCurrentUserImageUrl] = useState<string>(currentUser?.profileImage?.url || '');
    const [file, setFile] = useState<FileType>();
    const systemUser = new APICore().getLoggedInUser();
    const canEdit = systemUser.user.isAdmin;

    useEffect(() => {
        if (user) {
            const assignedRep = user.messageControl?.assignedRep;
            const derivedIsActive = user.messageControl?.isActive ?? !assignedRep;
            setCurrentUser({
                ...user,
                messageControl: {
                    ...user.messageControl,
                    isActive: derivedIsActive,
                    assignedRep,
                },
            });
        } else {
            setCurrentUser((prev) => {
                const nextUser = { ...(prev || {}) } as User;
                nextUser.isActive = true;
                nextUser.messageControl = nextUser.messageControl ?? { isActive: true };
                return nextUser;
            });
        }
    }, [user]);

    const onChange = (event: any) => {
        const name = event.target.name;
        const value = event.target.value;

        const tempUser: any = { ...currentUser };

        if (event.target.type === 'checkbox') {
            tempUser[name] = event.target.checked;
        } else {
            tempUser[name] = value;
        }

        setCurrentUser(tempUser);
    };

    function multiSelectChange(modules: any) {
        setCurrentUser((prev: any) => {
            return {
                ...prev,
                agentModules: modules.map((module: { value: string }) => module.value),
            };
        });
    }

    const onMessageAssignmentToggle = (checked: boolean) => {
        setCurrentUser((prev: any) => {
            const nextUser = { ...(prev || {}) };
            nextUser.messageControl = {
                ...(nextUser.messageControl || {}),
                isActive: checked,
                assignedRep: checked ? undefined : nextUser.messageControl?.assignedRep,
            };
            return nextUser;
        });
    };

    const onAssignedRepChange = (selectedOption: Option | null) => {
        setCurrentUser((prev: any) => {
            const nextUser = { ...(prev || {}) };
            nextUser.messageControl = {
                ...(nextUser.messageControl || {}),
                assignedRep: selectedOption ? selectedOption.value : undefined,
                isActive: selectedOption ? false : nextUser.messageControl?.isActive ?? true,
            };
            return nextUser;
        });
    };

    const messageControlActive = currentUser?.messageControl?.isActive ?? !currentUser?.messageControl?.assignedRep;
    const assignedRepValue = currentUser?.messageControl?.assignedRep;
    const assignmentSelectOptions = assignmentCandidates
        .filter((candidate) => candidate._id !== currentUser?._id)
        .map((candidate) => ({
            value: candidate._id,
            label: candidate.fullName || `${candidate.firstName} ${candidate.lastName}`,
        }));

    const selectedAssignmentOption = useMemo(() => {
        if (!assignedRepValue) {
            return null;
        }
        return assignmentSelectOptions.find((option) => option.value === assignedRepValue) || null;
    }, [assignedRepValue, assignmentSelectOptions]);

    const onSubmit = () => {
        // console.log('newUser', newUser)
        if (!currentUser) {
            return;
        }
        onAddUser?.(currentUser, user, file);
    };

    return (
        <Modal show={show} onHide={onHide} aria-labelledby="contained-modal-title-vcenter" centered size="lg">
            <Modal.Header className="bg-light" onHide={onHide} closeButton>
                <Modal.Title className="m-0">{user ? 'Edit User' : 'Add New User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <FormInput
                    disabled={!canEdit}
                    label="First Name"
                    type="text"
                    name="firstName"
                    defaultValue={currentUser?.firstName}
                    placeholder="Enter first name"
                    containerClass={'mb-3'}
                    onChange={onChange}
                />
                <FormInput
                    disabled={!canEdit}
                    label="Last Name"
                    type="text"
                    name="lastName"
                    defaultValue={currentUser?.lastName}
                    placeholder="Enter last name"
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Gender"
                    type="select"
                    name="gender"
                    value={currentUser?.gender}
                    placeholder="select gender"
                    containerClass={'mb-3'}
                    options={GenderOptions.map((type) => ({
                        label: camelCaseToText(type) || '',
                        value: type,
                    }))}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Email Address"
                    type="email"
                    name="email"
                    defaultValue={currentUser?.email}
                    placeholder="Enter email"
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Phone"
                    type="text"
                    name="phoneNumber"
                    defaultValue={currentUser?.phoneNumber}
                    placeholder="Enter phone number"
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <div className="form-label">Roles</div>
                <Select
                    isDisabled={!canEdit}
                    closeMenuOnSelect={false}
                    className="react-select react-select-container mb-3"
                    classNamePrefix="react-select"
                    value={currentUser?.roles?.map((role) => ({
                        label: role,
                        value: role,
                    }))}
                    isMulti
                    name="role"
                    options={['operator', 'agent', 'interviewer', 'manager', 'phoneCoordinator', 'support']
                        .concat(currentUser?.isSuperAdmin ? ['financialAdmin'] : [])
                        .map((type) => ({
                            label: camelCaseToText(type),
                            value: type,
                        }))}
                    onChange={(selectedRoles) => {
                        const roles = selectedRoles.map((role: Option) => role.value);
                        setCurrentUser((prev: any) => {
                            return { ...prev, roles };
                        });
                    }}
                />

                {currentUser && currentUser.roles?.includes('agent') && (
                    <FormInput
                        disabled={!canEdit}
                        label="Agent License"
                        type="text"
                        name="agentLicense"
                        defaultValue={currentUser?.agentLicense}
                        placeholder="Enter agent license"
                        containerClass={'mb-3'}
                        onChange={onChange}
                    />
                )}

                {currentUser && currentUser.roles?.some((role) => ['agent', 'operator', 'manager'].includes(role)) && (
                    <>
                        <div className="form-label">Modules</div>
                        <Select
                            isDisabled={!canEdit}
                            closeMenuOnSelect={false}
                            className="react-select react-select-container"
                            classNamePrefix="react-select"
                            value={currentUser?.agentModules?.map((module: any) => ({
                                label: module,
                                value: module,
                            }))}
                            isMulti
                            name="agentModules"
                            options={Object.entries(Modules).map((item) => ({
                                label: camelCaseToText(item[0]),
                                value: item[1],
                            }))}
                            onChange={multiSelectChange}
                        />
                        <br />
                    </>
                )}

                {currentUser?.roles?.some((role) => ['agent', 'interviewer'].includes(role)) && (
                    <>
                        <FormInput
                            disabled={!canEdit}
                            label="Meetings per day"
                            type="number"
                            name="meetingSlots"
                            defaultValue={currentUser?.meetingSlots}
                            containerClass={'mb-3'}
                            onChange={onChange}
                        />

                        <FormInput
                            disabled={!canEdit}
                            label="Personal Calendly Link"
                            type="text"
                            name="calendlyLink"
                            defaultValue={currentUser?.calendlyLink}
                            placeholder="Enter calendly link"
                            containerClass={'mb-3'}
                            onChange={onChange}
                        />

                        <FormInput
                            disabled={!canEdit}
                            label="Service Meeting Calendly Link"
                            type="text"
                            name="calendlyServiceMeetingLink"
                            defaultValue={currentUser?.calendlyServiceMeetingLink}
                            placeholder="Enter service meeting calendly link"
                            containerClass={'mb-3'}
                            onChange={onChange}
                        />
                    </>
                )}

                <FormInput
                    disabled={!canEdit}
                    label="Admin"
                    type="checkbox"
                    name="isAdmin"
                    // defaultChecked={currentUser?.isAdmin}
                    checked={currentUser?.isAdmin}
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Notify By WhatsApp"
                    type="checkbox"
                    name="notifyByWhatsapp"
                    // defaultChecked={currentUser?.isAdmin}
                    checked={currentUser?.notifyByWhatsapp}
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <div className="message-control-card border rounded p-3 mb-3">
                    <h5 className="mb-3">Message Assignment</h5>
                    <div className="form-check form-switch mb-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="messageControlActive"
                            checked={messageControlActive}
                            disabled={!canEdit}
                            onChange={(e) => onMessageAssignmentToggle(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="messageControlActive">
                            {messageControlActive ? 'Active' : 'Inactive'}
                        </label>
                    </div>

                    <label className="form-label">Assign messages to</label>
                    <Select
                        isDisabled={!canEdit || messageControlActive}
                        className="react-select react-select-container mb-2"
                        classNamePrefix="react-select"
                        value={selectedAssignmentOption}
                        placeholder="Select a team member..."
                        options={assignmentSelectOptions}
                        isClearable
                        onChange={onAssignedRepChange}
                    />
                    <small className="text-muted d-block mt-1">Inactive reps must assign an active teammate.</small>
                </div>

                <FormInput
                    disabled={!canEdit}
                    label="Active"
                    type="checkbox"
                    name="isActive"
                    checked={currentUser?.isActive}
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Module Responder"
                    type="checkbox"
                    name="isModuleResponder"
                    checked={currentUser?.isModuleResponder}
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <FormInput
                    disabled={!canEdit}
                    label="Exclude From Fake Onboarder Reassign"
                    type="checkbox"
                    name="excludeFromOnboardingAutoReassign"
                    checked={currentUser?.excludeFromOnboardingAutoReassign}
                    containerClass={'mb-3'}
                    onChange={onChange}
                />

                <label className="mb-1 mt-2">Image</label>
                <FileUploader
                    showPreview={false}
                    onFileUpload={(file: FileType) => {
                        setFile(file);
                        setCurrentUserImageUrl(URL.createObjectURL(file));
                    }}
                    currentImageUrl={currentUserImageUrl || user?.profileImage?.url}
                />

                {user &&
                    user._id &&
                    user.roles?.includes('agent') &&
                    (user.agentModules?.includes('insurance') || user.agentModules?.includes('pension')) && (
                        <InsuranceCompanyAgentManager userId={user._id} canEdit={!!canEdit} />
                    )}

                <div className="text-end mt-3">
                    <Button disabled={!canEdit} variant="success" onClick={onSubmit} className="waves-effect waves-light me-1">
                        Save
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddUser;
